import {
  Badge,
  Box,
  Grid,
  GridItem,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Skeleton,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { IconTrending5 } from 'central-icons/IconTrending5';
import React, { useCallback, useEffect, useState } from 'react';

import { FormatType, LineChart } from '@revelio/replots';

import type { OutageStatus } from '../healthCheck';
import {
  type HealthSummary,
  type ResponseTimesData,
  useHealthCheckApi,
} from '../hooks/useHealthCheckApi';
import { determineOutageStatus } from '../utils/health-check.utils';
import { TimelineDay } from './TimelineDay';

interface OverallHealthMetricsProps {
  summary: HealthSummary | null;
  isLoading: boolean;
}

export const OverallHealthMetrics: React.FC<OverallHealthMetricsProps> = ({
  summary,
  isLoading,
}) => {
  const {
    isOpen: isChartOpen,
    onOpen: onChartOpen,
    onClose: onChartClose,
  } = useDisclosure();

  const { fetchResponseTimes } = useHealthCheckApi();
  const [responseTimesData, setResponseTimesData] = useState<
    ResponseTimesData[]
  >([]);
  const [isLoadingChart, setIsLoadingChart] = useState(false);

  const loadResponseTimes = useCallback(async () => {
    setIsLoadingChart(true);
    try {
      const data = await fetchResponseTimes();
      setResponseTimesData(data);
    } finally {
      setIsLoadingChart(false);
    }
  }, [fetchResponseTimes]);

  useEffect(() => {
    if (isChartOpen && responseTimesData.length === 0) {
      loadResponseTimes();
    }
  }, [isChartOpen, responseTimesData.length, loadResponseTimes]);

  const timelineDays = React.useMemo(() => {
    const days: Array<{
      date: Date;
      status: OutageStatus | null;
      failureCount: number;
      totalChecks: number;
      avgResponseTime?: number;
    }> = [];

    const dailyStatsMap = new Map<
      string,
      {
        failureCount: number;
        totalChecks: number;
        avgResponseTime: number;
      }
    >();

    if (summary?.dailyStats) {
      summary.dailyStats.forEach((stat) => {
        dailyStatsMap.set(stat.date, {
          failureCount: stat.failureCount,
          totalChecks: stat.totalChecks,
          avgResponseTime: stat.avgResponseTime,
        });
      });
    }

    const now = new Date();
    for (let i = 89; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0] ?? '';
      const dayStats = dailyStatsMap.get(dateStr);

      days.push({
        date,
        status: dayStats ? determineOutageStatus(dayStats.failureCount) : null,
        failureCount: dayStats?.failureCount ?? 0,
        totalChecks: dayStats?.totalChecks ?? 0,
        avgResponseTime: dayStats?.avgResponseTime,
      });
    }

    return days;
  }, [summary?.dailyStats]);

  const overallStats = React.useMemo(() => {
    if (!summary) {
      return {
        uptime: '100.00',
        avgResponseTime: '0',
        currentStatus: 'operational' as OutageStatus,
      };
    }

    const uptimePercentage =
      summary.totalChecks > 0
        ? ((summary.totalChecks - summary.failureCount) / summary.totalChecks) *
          100
        : 100;

    return {
      uptime: uptimePercentage.toFixed(2),
      avgResponseTime: summary.avgResponseTime.toString(),
      currentStatus: summary.currentStatus,
    };
  }, [summary]);

  const responseTimeChartData = React.useMemo(() => {
    if (responseTimesData.length === 0) {
      return [];
    }

    const allDataPoints = responseTimesData.flatMap((service) =>
      service.data.map((point) => ({
        date: String(point.timestamp),
        value: point.responseTime,
      }))
    );

    const sortedData = allDataPoints.sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );

    return [
      {
        label: 'Response Time',
        values: sortedData,
        color: '#5BD992',
      },
    ];
  }, [responseTimesData]);

  const getStatusBadgeColor = (status: OutageStatus | 'loading'): string => {
    switch (status) {
      case 'operational':
        return 'green';
      case 'partial_outage':
        return 'yellow';
      case 'major_outage':
        return 'red';
      default:
        return 'gray';
    }
  };

  return (
    <Box
      p={6}
      borderRadius="lg"
      border="1px"
      borderColor="gray.200"
      bg="white"
      mb={8}
    >
      <VStack align="stretch" spacing={6}>
        {/* Status and Stats */}
        <Grid templateColumns="repeat(2, 1fr)" gap={6} alignItems="center">
          <GridItem display="flex" alignItems="center">
            <HStack spacing={2} align="center" width="100%">
              <Text fontSize="sm" color="gray.600" lineHeight="1">
                Current Status:
              </Text>
              <Badge
                colorScheme={getStatusBadgeColor(
                  isLoading ? 'loading' : overallStats.currentStatus
                )}
                fontSize="sm"
                px={2}
                py={0.5}
                lineHeight="1.2"
              >
                {isLoading
                  ? 'Loading...'
                  : overallStats.currentStatus
                      .split('_')
                      .map(
                        (word) => word.charAt(0).toUpperCase() + word.slice(1)
                      )
                      .join(' ')}
              </Badge>
            </HStack>
          </GridItem>
          <GridItem>
            <HStack justify="flex-end" align="center" width="100%">
              <IconButton
                aria-label="View response time chart"
                icon={<IconTrending5 />}
                size="md"
                variant="ghost"
                onClick={onChartOpen}
              />
            </HStack>
          </GridItem>
        </Grid>

        {/* Timeline */}
        <Box>
          <Box position="relative">
            {/* Timeline bars */}
            <Box position="relative" zIndex={2}>
              <HStack
                spacing={0.75}
                height="20px"
                justify="space-between"
                mx={0}
              >
                {timelineDays.map((day) => (
                  <TimelineDay
                    key={day.date.toISOString()}
                    status={day.status}
                    date={day.date}
                    failureCount={day.failureCount}
                    avgResponseTime={day.avgResponseTime}
                    isOverall={true}
                  />
                ))}
              </HStack>
            </Box>

            {/* Labels and connecting line container */}
            <Box position="relative" mt={4}>
              {/* Continuous line */}
              <Box
                position="absolute"
                top="50%"
                left="0"
                right="0"
                height="1px"
                bg="gray.200"
                transform="translateY(-50%)"
              />

              {/* Labels container */}
              <HStack
                justify="space-between"
                position="relative"
                mx={0}
                width="100%"
              >
                <Text fontSize="xs" color="gray.500" bg="white" pr={2}>
                  90 days ago
                </Text>
                <Box
                  position="absolute"
                  left="50%"
                  transform="translateX(-50%)"
                  bg="white"
                  px={2}
                >
                  <HStack spacing={1}>
                    <Text fontSize="xs" fontWeight="medium" color="gray.700">
                      {overallStats.uptime}%
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      uptime
                    </Text>
                  </HStack>
                </Box>
                <Text fontSize="xs" color="gray.500" bg="white" pl={2}>
                  Today
                </Text>
              </HStack>
            </Box>
          </Box>
        </Box>

        {/* Response Time Chart Modal */}
        <Modal isOpen={isChartOpen} onClose={onChartClose} size="xl">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Overall Response Time History</ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              <Box height="400px">
                {isLoadingChart && <Skeleton height="100%" width="100%" />}
                {!isLoadingChart && responseTimeChartData.length > 0 && (
                  <LineChart
                    data={responseTimeChartData}
                    format={FormatType.INTEGER}
                    dateFormat="daytime"
                    showGridLines={true}
                  />
                )}
                {!isLoadingChart && responseTimeChartData.length === 0 && (
                  <Text color="gray.500" textAlign="center" pt={8}>
                    No response time data available
                  </Text>
                )}
              </Box>
              <Text fontSize="sm" color="gray.500" mt={4}>
                Shows response times across all services. Lower is better.
              </Text>
            </ModalBody>
          </ModalContent>
        </Modal>
      </VStack>
    </Box>
  );
};
